import { useState, useMemo, useCallback, Fragment } from "react";
import useAuth from "../../../Hooks/useAuth";
import { useDebounce } from "../../../Hooks/useDebounce";
import { useGetPaymentOperationLogsPaginatedQuery } from "../../../store";
import { localDayStartISO, localDayEndISO } from "../../../utils/localDayRange";
import {
  PageShell,
  PageHeader,
  ListSkeleton,
  EmptyState,
  Drawer,
  ListView,
  ListToolbar,
} from "../../../Components/shared";
import { Pagination, CustomDropdown, DateRangeField } from "../../../Components/atoms";
import { FaLayerGroup, FaShoppingCart, FaUndo, FaListUl } from "react-icons/fa";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import trLocale from "date-fns/locale/tr";
import {
  TypeBadge,
  AmountText,
  formatDate,
  formatTL,
} from "../../../Components/wallet/walletTxUi";
import { METHOD_LABEL, toMachineRecordRow as toRow } from "../../../utils/machineRecord";

const METHOD_OPTIONS = [
  { id: "all", value: "", label: "Tüm Yöntemler" },
  { id: "app_qr", value: "app_qr", label: "BayinerPay QR" },
  { id: "app_personal_card", value: "app_personal_card", label: "BayinerPay NFC" },
];

const FILTERS = [
  { id: "all", label: "Tümü", icon: FaLayerGroup },
  { id: "payment", label: "Satış", icon: FaShoppingCart },
  { id: "refund", label: "İade", icon: FaUndo },
];

const TABLE_COLUMNS = [
  { label: "Tarih", width: "w-40", skeletonWidth: "w-24" },
  { label: "Makine", width: "w-44", skeletonWidth: "w-28" },
  { label: "Tür", width: "w-24", skeletonWidth: "w-16" },
  { label: "Ödeme Yöntemi", width: "w-32", skeletonWidth: "w-20" },
  { label: "Ürün(ler)", width: "", skeletonWidth: "w-32", skeletonExtra: true },
  { label: "Tutar", width: "w-28", skeletonWidth: "w-16" },
  { label: "Fatura No", width: "w-36", skeletonWidth: "w-24" },
];

// Raporlardaki DatePicker görünümüyle aynı stil


function itemsSummary(items) {
  if (!items.length) return "-";
  const first = items[0]?.productName || items[0]?.name || "Ürün";
  return items.length > 1 ? `${first} +${items.length - 1}` : first;
}

function DetailRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <span className="text-sm text-gray-800 font-medium text-right break-words">
        {value}
      </span>
    </div>
  );
}

function RecordDetailDrawer({ row, onClose }) {
  const isRefund = row?.txType === "refund";
  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width="md"
      title={isRefund ? "İade Kaydı" : "Satış Kaydı"}
      subtitle={row?.serialNo || ""}
      titleIcon={
        isRefund ? (
          <FaUndo className="w-5 h-5 text-main" />
        ) : (
          <FaShoppingCart className="w-5 h-5 text-main" />
        )
      }
    >
      {row && (
        <Drawer.Body className="p-5 space-y-5">
          {/* Üst: tutar + tür */}
          <div className="flex items-center justify-between">
            <AmountText tx={row} className="text-2xl" />
            <TypeBadge txType={row.txType} />
          </div>

          {/* Operasyon */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Operasyon
            </h4>
            <DetailRow label="Açıklama" value={row.description} />
            <DetailRow label="Makine (Seri No)" value={row.serialNo} />
            <DetailRow label="Makine Adı" value={row.deviceName} />
            <DetailRow label="Ödeme Yöntemi" value={row.methodLabel} />
            <DetailRow label="Tarih" value={formatDate(row.date)} />
          </div>

          {/* Referans */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Referans
            </h4>
            <DetailRow label="Fatura No" value={row.reference} />
            <DetailRow label="Intent ID" value={row.intentId} />
          </div>

          {/* İade bilgisi */}
          {row.refund && (
            <div className="bg-blue-50 rounded-xl p-3">
              <h4 className="text-xs font-semibold text-blue-500 uppercase tracking-wider mb-1">
                İade Bilgisi
              </h4>
              <DetailRow
                label="İade Tutarı"
                value={formatTL(Number(row.refund.price || 0) / 100)}
              />
              <DetailRow label="Adet" value={row.refund.count} />
              <DetailRow
                label="Yöntem"
                value={METHOD_LABEL[row.refund.paymentMethod] || row.refund.paymentMethod}
              />
            </div>
          )}

          {/* Ürünler */}
          {row.items.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Ürünler
              </h4>
              <div className="space-y-1.5">
                {row.items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-gray-800 font-medium truncate">
                        {item.productName || item.name || "Ürün"}
                        {item.payBack ? (
                          <span className="ml-2 text-[10px] font-semibold text-blue-600 uppercase">
                            iade
                          </span>
                        ) : null}
                      </div>
                      {(item.volume || item.vessel) && (
                        <div className="text-xs text-gray-500 truncate">
                          {[item.volume, item.vessel].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    {item.price != null && (
                      <span className="text-sm font-semibold text-gray-700 shrink-0 ml-2">
                        {formatTL(Number(item.price) / 100)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* İşlem adımları (zaman çizelgesi) */}
          {row.steps.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                İşlem Adımları
              </h4>
              <ol className="relative border-l border-gray-200 ml-2 space-y-3">
                {row.steps.map((step, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-[5px] w-2.5 h-2.5 rounded-full bg-primary" />
                    <div className="text-sm text-gray-800">
                      {step.message || step.type || "-"}
                    </div>
                    <div className="text-xs text-gray-400">
                      {step.type ? `${step.type} · ` : ""}
                      {formatDate(step.createdInfo)}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </Drawer.Body>
      )}
    </Drawer>
  );
}

// `embedded`: PaymentsPage sekmesi olarak render edilirken kendi PageShell'ini
// KURMAZ (kabuk ust sayfada). Kendi PageHeader/stat blogu sekme icinde kalir.
function MachineRecordsPage({ embedded = false }) {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const pageSize = 20;

  const [activeFilter, setActiveFilter] = useState("all");
  const [method, setMethod] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [selected, setSelected] = useState(null);
  const search = useDebounce(searchInput.trim(), 400);

  // Tarihler yerel gün sınırının ISO'su — "YYYY-MM-DD" UTC okunup 3 saat
  // kayıyordu (#242)
  const startISO = localDayStartISO(startDate);
  const endISO = localDayEndISO(endDate);

  // Sayfa filtre anahtarına bağlı: filtre değişince AYNI render'da 1'e döner
  // (önce eski sayfayla + sonra 1 ile iki istek gidiyordu)
  const filterKey = JSON.stringify([activeFilter, method, startISO, endISO, search]);
  const [pageState, setPageState] = useState({ key: filterKey, page: 1 });
  const currentPage = pageState.key === filterKey ? pageState.page : 1;
  const setCurrentPage = useCallback(
    (page) => setPageState({ key: filterKey, page }),
    [filterKey]
  );

  // Query: argümana göre anahtarlanır, geç gelen eski yanıt listeyi ezmez
  const { data: apiData, isFetching: isLoading } = useGetPaymentOperationLogsPaginatedQuery(
    {
      token,
      page: currentPage - 1,
      limit: pageSize,
      txType: activeFilter === "all" ? undefined : activeFilter,
      paymentMethod: method || undefined,
      startDate: startISO,
      endDate: endISO,
      search: search || undefined,
    },
    { skip: !token }
  );

  const rows = useMemo(
    () => (apiData?.data || []).map(toRow),
    [apiData],
  );
  const total = apiData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  const stats = useMemo(
    () => [{ icon: FaListUl, value: total, label: "Kayıt", color: "blue" }],
    [total],
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      const tableContainer = document.querySelector(".overflow-y-auto");
      if (tableContainer) tableContainer.scrollTop = 0;
    },
    [setCurrentPage]
  );

  // Yöntem + tarih aralığı — raporlardaki tarih seçicilerle aynı görünüm.
  // ListToolbar `search` node'u: masaüstünde arama satırında, mobilde filtre
  // modalının içinde görünür.
  const extraFiltersNode = (
    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:items-end">
      <div className="sm:w-44">
        <CustomDropdown
          label="Ödeme Yöntemi"
          compact={true}
          value={METHOD_OPTIONS.find((o) => o.value === method) || METHOD_OPTIONS[0]}
          onChange={(v) => setMethod(v.value)}
          options={METHOD_OPTIONS}
          placeholder="Yöntem seçin"
          showSearch={false}
        />
      </div>
      <DateRangeField
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        size="sm"
        zIndex={10050}
        className="flex flex-col sm:flex-row gap-2 [&>div]:sm:w-44"
      />
    </div>
  );

  const Shell = embedded ? Fragment : PageShell;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
      <Shell>
        {/* Sekme olarak gomuluyken baslik ustte (PaymentsPage) tek sefer
            durur; burada ayri bir baslik satiri olmaz ve istatistik rozetleri
            asagida filtre butonlariyla AYNI satira gecer. */}
        {!embedded && (
          <div className="px-3 py-2.5 sm:p-4 md:p-6 bg-gray-50/80 sm:bg-gray-50 shrink-0">
            <PageHeader
              icon={FaListUl}
              iconColor="emerald"
              title="Makine Kayıtları"
              subtitle="Makinelerdeki ödeme ve iade işlem kayıtları"
              stats={stats}
              isLoading={isLoading}
              mobileSlotId="machine-records-toolbar-mobile-slot"
            />
          </div>
        )}

        <div className="bg-white flex-1 min-h-0 flex flex-col">
          <ListToolbar
            className={
              embedded
                ? "px-3 pt-2.5 pb-2.5 sm:px-4 sm:pt-4 sm:pb-4 md:px-6 md:pt-4 md:pb-6"
                : "px-3 pb-2.5 sm:px-4 sm:pb-4 md:px-6 md:pb-6"
            }
            mobileFilterSlotId="machine-records-toolbar-mobile-slot"
            stats={embedded ? stats : undefined}
            filterTabs={{
              filters: FILTERS,
              activeFilter,
              onChange: setActiveFilter,
              totalCount: total,
              isLoading,
              color: "emerald",
            }}
            search={extraFiltersNode}
            searchFields={[
              {
                placeholder: "Seri No, Fatura No, Intent ID",
                value: searchInput,
                onChange: (e) => setSearchInput(e.target.value),
              },
            ]}
            onClear={() => {
              setActiveFilter("all");
              setMethod("");
              setStartDate(null);
              setEndDate(null);
              setSearchInput("");
            }}
          />

          <ListView
            headers={TABLE_COLUMNS.map((c) => ({ label: c.label, className: c.width }))}
            data={rows}
            rowKey="_id"
            renderRow={(row) => (
              <tr
                key={row._id}
                className="bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelected(row)}
              >
                <td className="px-4 py-3 text-sm text-gray-500">
                  {formatDate(row.date)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">
                  <div className="font-mono text-gray-800 truncate">
                    {row.serialNo || "-"}
                  </div>
                  {row.deviceName && (
                    <div className="text-xs text-gray-400 truncate">
                      {row.deviceName}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <TypeBadge txType={row.txType} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {row.methodLabel}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 truncate">
                  {itemsSummary(row.items)}
                </td>
                <td className="px-4 py-3 text-sm">
                  <AmountText tx={row} />
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 font-mono truncate">
                  {row.reference || "-"}
                </td>
              </tr>
            )}
            renderMobileCard={(row) => (
              <div
                className="bg-white px-3.5 py-3 border-b border-gray-200 active:bg-gray-50 cursor-pointer"
                onClick={() => setSelected(row)}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <AmountText tx={row} className="text-sm" />
                  <TypeBadge txType={row.txType} />
                </div>
                <div className="text-sm text-gray-700 truncate">
                  {itemsSummary(row.items)}
                </div>
                <div className="flex items-center justify-between mt-1.5 text-xs text-gray-400">
                  <span className="font-mono truncate">
                    {row.serialNo || "-"} · {row.methodLabel}
                  </span>
                  <span>{formatDate(row.date)}</span>
                </div>
              </div>
            )}
            isLoading={isLoading}
            loadingNode={
              <div className="relative p-3 sm:p-4 md:p-6">
                <ListSkeleton
                  columns={TABLE_COLUMNS}
                  rowCount={10}
                  mobileCardCount={6}
                  message="Makine kayıtları yükleniyor..."
                />
              </div>
            }
            empty={
              <div className="p-4 md:p-6">
                <EmptyState
                  message="Kayıt bulunamadı"
                  suggestion="Seçili filtrelere uygun ödeme/iade kaydı yok."
                />
              </div>
            }
            footer={
              totalPages > 1 ? (
                <div className="shrink-0 border-t border-gray-200 bg-white">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={pageSize}
                    totalItems={total}
                    showInfo={true}
                    disabled={isLoading}
                  />
                </div>
              ) : null
            }
          />
        </div>

        <RecordDetailDrawer row={selected} onClose={() => setSelected(null)} />
      </Shell>
    </LocalizationProvider>
  );
}

export default MachineRecordsPage;
